import { Box, SxProps, Theme } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { CSSProperties, FC, memo, useCallback, useMemo, useRef } from "react";
import { FilterSelectOption, MENU_ITEM_HEIGHT } from "./FilterSelectOption";

const OVERSCAN = 5;

interface FilterSelectOptionListProps {
  options: Array<{ value: string; label: string }>;
  tempSelectedValues: string[];
  handleToggleOption: (value: string) => void;
  maxHeight: number | string;
  sx?: SxProps<Theme>;
}

interface VirtualOptionRowProps {
  value: string;
  label: string;
  isSelected: boolean;
  start: number;
  onToggle: (value: string) => void;
}

const VirtualOptionRow: FC<VirtualOptionRowProps> = memo(
  ({ value, label, isSelected, start, onToggle }) => {
    const handleClick = useCallback(() => onToggle(value), [onToggle, value]);
    const style = useMemo<CSSProperties>(
      () => ({
        left: 0,
        position: "absolute",
        top: 0,
        transform: `translateY(${start}px)`,
        width: "100%",
      }),
      [start],
    );

    return (
      <div style={style}>
        <FilterSelectOption
          isSelected={isSelected}
          label={label}
          onClick={handleClick}
        />
      </div>
    );
  },
);

export const FilterSelectOptionList: FC<FilterSelectOptionListProps> = ({
  options,
  tempSelectedValues,
  handleToggleOption,
  maxHeight,
  sx,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(
    () => new Set(tempSelectedValues),
    [tempSelectedValues],
  );

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => MENU_ITEM_HEIGHT,
    overscan: OVERSCAN,
  });

  return (
    <Box ref={scrollRef} sx={{ maxHeight, overflowY: "auto", ...sx }}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: "relative",
          width: "100%",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const option = options[virtualRow.index];
          if (!option) return null;

          return (
            <VirtualOptionRow
              isSelected={selectedSet.has(option.value)}
              key={option.value}
              label={option.label}
              onToggle={handleToggleOption}
              start={virtualRow.start}
              value={option.value}
            />
          );
        })}
      </div>
    </Box>
  );
};
